import { useEffect, useState } from "react";
import { GitBranch, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchAnalyse } from "@/lib/entreprises";
import { fetchMemoParams, saveMemoParams } from "@/lib/memo";

export default function IntraGroupeCard({ dossierId, siren }) {
    const [societes, setSocietes] = useState([]);
    const [choix, setChoix] = useState("");
    const [texte, setTexte] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchMemoParams(dossierId).then((p) => setTexte(p.historique_intra_groupe || "")).catch(() => {});
        if (siren) fetchAnalyse(siren).then((a) => setSocietes((a.groupe?.societes || []).filter((s) => !s.auditee))).catch(() => {});
    }, [dossierId, siren]);

    function addSociete(s) {
        setChoix(s);
        const soc = societes.find((x) => x.siren === s);
        if (soc && !texte.includes(soc.nom_entreprise)) setTexte((t) => `${t ? t + "\n\n" : ""}## ${soc.nom_entreprise} (SIREN ${soc.siren})\n`);
    }

    async function save() {
        setSaving(true);
        try { await saveMemoParams(dossierId, { historique_intra_groupe: texte }); toast.success("Historique intra-groupe enregistré"); }
        catch (e) { toast.error("Enregistrement impossible", { description: e.message }); }
        finally { setSaving(false); }
    }

    return (
        <Card data-testid="intra-groupe-card" className="border-slate-200 bg-white shadow-sm">
            <CardContent className="pt-6 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center"><GitBranch className="h-4 w-4 text-primary" strokeWidth={1.75} /></div>
                    <div className="flex-1">
                        <h2 className="font-display text-xl text-foreground">Liens &amp; Historique Intra-groupe</h2>
                        <p className="text-xs text-muted-foreground">Choisissez une société du groupe (Pappers) puis décrivez les liens : conventions de trésorerie, management fees, cautions, historique.</p>
                    </div>
                    <Button type="button" data-testid="intra-groupe-save-top" onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
                    </Button>
                </div>
                <Select value={choix} onValueChange={addSociete}>
                    <SelectTrigger data-testid="intra-groupe-select" className="h-10 bg-card max-w-md"><SelectValue placeholder={societes.length ? "Ajouter une société du groupe…" : "Aucune société de groupe détectée (lancer l'analyse Pappers)"} /></SelectTrigger>
                    <SelectContent>{societes.map((s) => <SelectItem key={s.siren} value={s.siren} data-testid={`intra-groupe-option-${s.siren}`}>{s.nom_entreprise} · {s.siren}</SelectItem>)}</SelectContent>
                </Select>
                <Textarea data-testid="intra-groupe-text" rows={8} value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Historique des flux et liens intra-groupe…" className="bg-card resize-y text-sm" />
                <Button type="button" data-testid="intra-groupe-save" onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
                </Button>
            </CardContent>
        </Card>
    );
}
